import { InboxOutlined } from "@ant-design/icons";
import { Form, Input, Radio } from "antd";
import TextArea from "antd/es/input/TextArea";
import Dragger from "antd/es/upload/Dragger";
import { FormItemProps } from "antd/lib";

export const STATUS_MESSAGES = {
  SCRAPING: "Failed to scrape.",
  EXTRACTING: "Unable to extract the data.",
  INVALID_FILE_TYPE: "Invalid File Type.",
  UNKNOWN: "",
};

export type TaskInputForm = {
  title: string;
  type: "text" | "url" | "pdf"; // Changed 'file' to 'pdf' for consistency
  description?: string;
  url?: string;
  file?: File | null; // Represents the single file object
  crawl?: boolean;
};

// Rename and update the type to include necessary Form.Item props
export type TaskInputProps<T = TaskInputForm> = Pick<
  FormItemProps<T>,
  | "label"
  | "name"
  | "valuePropName"
  | "getValueFromEvent"
  | "trigger"
  | "rules"
  | "normalize"
> & {
  children: React.ReactNode; // The actual input component (Input, Dragger, TextArea)
};

export const createFileInputProps = ({
  label,
  accept,
  hintText = "Supports a single file upload.",
}: {
  label: string;
  accept: ".pdf" | ".csv";
  hintText?: string;
}): TaskInputProps => ({
  label,
  name: "file",
  valuePropName: "fileList",
  getValueFromEvent: (e) => e?.fileList?.[0]?.originFileObj || null,
  trigger: "onChange",
  normalize: (value: File | null) => {
    if (value instanceof File) {
      return [
        {
          uid: "-1",
          name: value.name,
          status: "done",
          originFileObj: value,
        },
      ];
    }
    return [];
  },
  children: (
    <Dragger beforeUpload={() => false} accept={accept} maxCount={1}>
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">
        Click or drag a file to this area to upload
      </p>
      <p className="ant-upload-hint">{hintText}</p>
    </Dragger>
  ),
});

export const createUrlInputWithCrawlProps = (): Pick<
  TaskInputProps,
  "label" | "children"
> => ({
  label: "URL",
  children: (
    // Use a fragment because Form.Item needs a single child,
    // but we have multiple controls (Input + Radio Group)
    <>
      {/* URL Input - No name needed here as parent Form.Item handles it */}
      <Input placeholder="Drop a link to your website, blog post, news article, etc." />
      {/* Crawl Options - Needs its own Form.Item wrapper */}
      <Form.Item name="crawl" className="mt-2 mb-0" initialValue={false}>
        <Radio.Group id="crawl">
          <Radio value={true}>Scan entire site</Radio>
          <Radio value={false}>Scan single page</Radio>
        </Radio.Group>
      </Form.Item>
    </>
  ),
});

export const createTextContentInputProps = (): TaskInputProps => ({
  label: "Text Content",
  name: "description",
  children: (
    <TextArea
      placeholder="Paste your text content here..."
      rows={4}
      maxLength={5000} // Example max length
    />
  ),
});
